import { useMemo } from 'react';

import { Alert, Badge, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';

import { describeRegistryPrice, REGISTRY_ORDER_STATUS_META } from '@/types/registry-documents';
import { SubmitErrorAlert, whenSent } from './ConfirmStep';
import {
  batchTotals,
  countryNameFor,
  describeSharedPrice,
  type BatchCompany,
  type CountryTotal,
  type FirmTotal,
} from './orderDraft';
import type { BatchRow } from './useBatchSubmit';
import type { OrderSharedDocuments } from './useOrderSharedDocuments';

interface BatchConfirmStepProps {
  companies: BatchCompany[];
  documents: OrderSharedDocuments;
  reportActivated: boolean | null;
  /** One per company once the batch is sent; empty before. */
  rows: BatchRow[];
  inFlight: boolean;
  onOrderAnyway: (index: number) => void;
}

/** Step 3 when ordering for every company - what is ordered for whom, what
 * it costs, and, once sent, where each order stands. */
export function BatchConfirmStep({
  companies,
  documents,
  reportActivated,
  rows,
  inFlight,
  onOrderAnyway,
}: Readonly<BatchConfirmStepProps>) {
  const totals = useMemo(
    () => batchTotals(companies, documents.chosen),
    [companies, documents.chosen]
  );
  const started = rows.length > 0;
  return (
    <Stack gap="sm" mt="sm" data-testid="registry-order-batch-summary">
      <Stack gap={6}>
        {companies.map((company, index) => (
          <CompanyLine
            key={company.subject.id}
            company={company}
            row={rows[index]}
            reportActivated={reportActivated}
            inFlight={inFlight}
            onOrderAnyway={() => onOrderAnyway(index)}
          />
        ))}
      </Stack>
      <DocumentsSummary documents={documents} totals={totals} />
      {documents.quoteCount > 0 && (
        <Alert color="orange" variant="light">
          {documents.quoteCount === 1 ? 'One document is' : `${documents.quoteCount} documents are`}{' '}
          priced on quote in at least one country: Armadillo emails the price and turnaround for
          that order first, and nothing is fulfilled until it is accepted. Such an order carries no
          list price until then.
        </Alert>
      )}
      {!started && (
        <Alert color="blue" variant="light">
          {whenSent(reportActivated, 'Each order')}
        </Alert>
      )}
      <BatchProgress rows={rows} inFlight={inFlight} reportActivated={reportActivated} />
    </Stack>
  );
}

function CompanyLine({
  company,
  row,
  reportActivated,
  inFlight,
  onOrderAnyway,
}: Readonly<{
  company: BatchCompany;
  row: BatchRow | undefined;
  reportActivated: boolean | null;
  inFlight: boolean;
  onOrderAnyway: () => void;
}>) {
  const failed = row?.status === 'duplicate' || row?.status === 'failed';
  return (
    <Stack gap={4} data-testid={`registry-order-batch-${company.subject.id}`}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="wrap">
          <Text size="sm" fw={600}>
            {company.subject.name}
          </Text>
          <Text size="sm" c="dimmed">
            · {countryNameFor(company.country)}
          </Text>
          <Tooltip label="Ordered by name - Armadillo's research desk identifies the company from the details in the order note">
            <Badge size="xs" variant="light" color="grape">
              by name
            </Badge>
          </Tooltip>
        </Group>
        {row && <RowStatus row={row} reportActivated={reportActivated} />}
      </Group>
      {row && failed && (
        // One re-order at a time: the row's button waits while anything
        // is in flight.
        <SubmitErrorAlert
          error={row.error}
          submitting={inFlight}
          reportActivated={reportActivated}
          onOrderAnyway={onOrderAnyway}
          subject={company.subject}
        />
      )}
    </Stack>
  );
}

function sendingLabel(reportActivated: boolean | null): string {
  if (reportActivated === null) {
    return 'Ordering…';
  }
  return reportActivated ? 'Placing…' : 'Preparing…';
}

function RowStatus({
  row,
  reportActivated,
}: Readonly<{ row: BatchRow; reportActivated: boolean | null }>) {
  switch (row.status) {
    case 'pending':
      return (
        <Badge size="xs" variant="light" color="gray">
          Waiting
        </Badge>
      );
    case 'sending':
      return (
        <Group gap={6} wrap="nowrap">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">
            {sendingLabel(reportActivated)}
          </Text>
        </Group>
      );
    case 'done': {
      const meta = REGISTRY_ORDER_STATUS_META[row.order.status];
      return (
        <Badge size="xs" variant="light" color={meta.color}>
          {meta.label} · {row.order.reference}
        </Badge>
      );
    }
    case 'duplicate':
      return (
        <Badge size="xs" variant="light" color="yellow">
          Already ordered
        </Badge>
      );
    default:
      return (
        <Badge size="xs" variant="light" color="red">
          Failed
        </Badge>
      );
  }
}

function DocumentsSummary({
  documents,
  totals,
}: Readonly<{
  documents: OrderSharedDocuments;
  totals: { perCountry: CountryTotal[]; grand: FirmTotal | null };
}>) {
  return (
    <Stack gap={2}>
      {documents.chosen.map((product) => (
        <Group key={product.code} justify="space-between">
          <Text size="sm">{product.description ?? product.code}</Text>
          <Text size="sm">{describeSharedPrice(product)}</Text>
        </Group>
      ))}
      {totals.perCountry.map(({ country, total }) => (
        <Group key={country} justify="space-between">
          <Text size="sm" c="dimmed">
            List price per order · {countryNameFor(country)}
          </Text>
          <Text size="sm" c="dimmed">
            {total ? describeRegistryPrice(total.amount, total.currency, false) : 'On quote'}
          </Text>
        </Group>
      ))}
      {totals.grand && (
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            List price, all orders
          </Text>
          <Text size="sm" fw={600} data-testid="registry-order-batch-total">
            {describeRegistryPrice(totals.grand.amount, totals.grand.currency, false)}
          </Text>
        </Group>
      )}
    </Stack>
  );
}

/** The one line that says where the batch stands: which order is going
 * out, then how many went through. Empty before the batch is sent. */
function progressLine(rows: BatchRow[], inFlight: boolean, reportActivated: boolean | null) {
  const total = rows.length;
  const orders = total === 1 ? 'order' : 'orders';
  if (total === 0) {
    return '';
  }
  if (inFlight) {
    const sending = rows.findIndex((row) => row.status === 'sending');
    return sending === -1
      ? `Sending ${total} ${orders}…`
      : `Sending order ${sending + 1} of ${total}…`;
  }
  const done = rows.filter((row) => row.status === 'done').length;
  const verb = reportActivated === true ? 'placed' : 'prepared';
  const rest = done < total ? ' The rest are listed above with what went wrong.' : '';
  return `${done} of ${total} ${orders} ${verb}.${rest}`;
}

/** Progress as a live region, mounted before the first order goes out so
 * assistive technology hears each change: the rows' badges are visual only.
 * The window is locked while orders are in flight, so the dimmed line says
 * what is true of an order already sent, not that the window can be closed. */
function BatchProgress({
  rows,
  inFlight,
  reportActivated,
}: Readonly<{ rows: BatchRow[]; inFlight: boolean; reportActivated: boolean | null }>) {
  return (
    <Stack gap={2}>
      <Text
        size="sm"
        role="status"
        aria-live="polite"
        aria-atomic
        data-testid="registry-order-batch-progress"
      >
        {progressLine(rows, inFlight, reportActivated)}
      </Text>
      {inFlight && (
        <Text size="xs" c="dimmed">
          Orders are sent one after another; an order already sent completes even if this window is
          closed.
        </Text>
      )}
    </Stack>
  );
}
